import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import type { IndividualPaymentSaveViewModel } from '@/shared/api/generated';
import { usePostApiV1IndividualpaymentFinancialPeople } from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/** Rota da listagem, cujo cache é invalidado após a criação. */
const PEOPLE_QUERY_URL = '/api/v1/individualpayment/people';

/**
 * Lê uma propriedade de um valor desconhecido sem recorrer a `any`.
 *
 * @param source - Valor de origem.
 * @param key - Nome da propriedade.
 * @returns O valor da propriedade, ou `undefined` quando a origem não é objeto.
 */
function readProperty(source: unknown, key: string): unknown {
  if (typeof source !== 'object' || source === null) return undefined;
  return (source as Record<string, unknown>)[key];
}

/**
 * Extrai o id do contrato financeiro criado a partir da resposta do `POST`.
 *
 * O spec descreve o `200` como `any`, então a resposta é inspecionada em vez de
 * confiada: pode vir como número puro ou como objeto com `id` /
 * `financialPersonId`. Quando nada casa, devolve `null` — e quem chama degrada
 * o destino em vez de navegar para uma URL com `NaN`.
 *
 * @param response - Corpo devolvido pelo `POST`.
 * @returns Id do contrato criado, ou `null`.
 *
 * @example
 * ```ts
 * readCreatedFinancialPersonId({ id: 101 }); // 101
 * readCreatedFinancialPersonId('ok');        // null
 * ```
 */
export function readCreatedFinancialPersonId(response: unknown): number | null {
  if (typeof response === 'number' && Number.isInteger(response) && response > 0) {
    return response;
  }

  for (const key of ['financialPersonId', 'id']) {
    const value = readProperty(response, key);
    if (typeof value === 'number' && Number.isInteger(value) && value > 0) return value;
  }

  return null;
}

/**
 * Criação de um plano financeiro individual.
 *
 * O corpo é o mesmo `IndividualPaymentSaveViewModel` do `PUT` da edição — é isso
 * que permite às duas telas compartilharem formulário e schema.
 *
 * Os estados de erro são separados de propósito. `businessMessage` carrega a
 * mensagem do `400`, que é a **única** explicação disponível para as regras de
 * criação (matrícula bloqueada em seleção de pagamento, entre outras): elas não
 * existem no frontend, e trocá-las por "erro ao salvar" deixaria o operador sem
 * saída. `isForbidden` isola o `403`, já que o login não devolve `canCreate` e a
 * recusa de escrita só aparece aqui. `isServerError` cobre o `5xx`, que **não**
 * tem tratamento global: o interceptor apenas repassa o erro adiante.
 *
 * @returns `{ create, isPending, isForbidden, isBusinessError, businessMessage, isServerError, reset }`
 *
 * @example
 * ```tsx
 * const { create, isPending } = useCreatePlan();
 * create(payload, {
 *   onSuccess: id => navigate({ to: `/individual-payment/${id}` }),
 *   onError: error => { if (error.kind === 'server') alert({ alertType: 'danger', title: '...' }); },
 * });
 * ```
 */
export function useCreatePlan() {
  const queryClient = useQueryClient();
  const mutation = usePostApiV1IndividualpaymentFinancialPeople();

  /**
   * Trava de envio em voo.
   *
   * É uma ref, e não `mutation.isPending`, porque o guard precisa valer **dentro
   * do mesmo tick**: dois cliques rápidos entram na mesma passada de eventos, e
   * `isPending` ainda é o valor do render anterior — `false` — quando a segunda
   * chamada lê o closure. A ref muda na hora, antes do `mutate`.
   */
  const inFlight = useRef(false);

  const create = useCallback(
    (
      data: IndividualPaymentSaveViewModel,
      options?: {
        readonly onSuccess?: (financialPersonId: number | null) => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      // A criação gera parcelas e emite Nosso Número: um segundo envio cria um
      // contrato duplicado cuja correção é manual.
      if (inFlight.current) return;
      inFlight.current = true;

      mutation.mutate(
        { data },
        {
          onSuccess: response => {
            void queryClient.invalidateQueries({ queryKey: [{ url: PEOPLE_QUERY_URL }] });
            options?.onSuccess?.(readCreatedFinancialPersonId(response));
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
          // Libera também no erro: uma recusa por regra de negócio precisa de
          // nova tentativa depois da correção, e travar aqui deixaria a tela
          // preenchida e inerte.
          onSettled: () => {
            inFlight.current = false;
          },
        },
      );
    },
    [mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    create,
    isPending: mutation.isPending,
    isForbidden: failure.kind === 'forbidden',
    /** `true` no `400` — inclusive quando o corpo não trouxe mensagem legível. */
    isBusinessError: failure.kind === 'business',
    /** Mensagem do backend; `null` quando o `400` veio sem corpo interpretável. */
    businessMessage: failure.kind === 'business' ? failure.message : null,
    /** `true` no `5xx` — falha do servidor, não do que o operador preencheu. */
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
